import React, { useState } from 'react';
import styled from 'styled-components';
import { Breakpoints, Color, Grid } from '../../../app/StyleGuide';
import { DeemphasizedBadge } from '../../../components/Badge';
import { OutlineButton, SmallRoundedIconButton } from '../../../components/Buttons';
import { MaterialList, MaterialListItem } from '../../../components/CurriculumMaterialList';
import { SubHeaderWithButton } from '../../../components/Layout';
import curriculumService from '../../../services/curriculumService';
import { AdvancePlanningSection } from '../models/advancePlanning';

type VolumeHeadersSectionProps = {
  section: AdvancePlanningSection;
  initializeOpen?: boolean;
};

const VolumeSectionHeaderContainer = styled(SubHeaderWithButton)<{ showContents: boolean }>`
  padding: ${Grid._5} 0;

  &:not(:last-child) {
    border-bottom: ${props => (props.showContents ? 'none' : `1px solid ${Color.Gray._10}`)};
  }

  h4 {
    margin: 0;
  }
`;

const HeaderTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Grid._4};
  margin-bottom: ${Grid._5};

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    margin-bottom: 0;
  }
`;

const UpDownIcon = styled.i.attrs({ className: 'fas fa-chevron-down' })<{ isUp: boolean }>`
  transform: rotate(${props => (props.isUp ? '180' : '0')}deg);
`;

// eslint-disable-next-line react/display-name
export const VolumeHeadersSection = React.forwardRef<HTMLDivElement, VolumeHeadersSectionProps>(
  ({ section, initializeOpen = false }, ref) => {
    const [showContents, setShowContents] = useState(initializeOpen);

    const download = (path: string, name: string) => curriculumService.downloadVolumeMaterial(path, name);

    return (
      <div ref={ref} data-tracking-component={'volume-header'} data-tracking-id={section.header}>
        <VolumeSectionHeaderContainer showContents={showContents}>
          <HeaderTitleContainer>
            <SmallRoundedIconButton onClick={() => setShowContents(prev => !prev)} aria-label="toggle contents">
              <UpDownIcon isUp={showContents} />
            </SmallRoundedIconButton>
            <h4>{section.header}</h4>
          </HeaderTitleContainer>
          {section.archivePath && (
            <OutlineButton onClick={() => download(section.archivePath, section.archiveName)}>
              Download All
            </OutlineButton>
          )}
        </VolumeSectionHeaderContainer>
        {showContents && (
          <MaterialList>
            {section.materials
              .sort((a, b) => Number(!!b.fileName) - Number(!!a.fileName))
              .map(material => {
                const isPlus = material.package === 'plus';
                const hasAccess = !!material.filePath;
                return (
                  <MaterialListItem
                    key={material.materialId}
                    disabled={!hasAccess}
                    onClick={hasAccess ? () => download(material.filePath, material.fileName) : undefined}
                    data-tracking-component="volume-material"
                    data-tracking-id={material.name}
                  >
                    <div className="material-text">
                      <p className="material-name">
                        {material.name}
                        {isPlus && <DeemphasizedBadge style={{ marginLeft: '8px' }}>Plus</DeemphasizedBadge>}
                      </p>
                    </div>
                    <SmallRoundedIconButton disabled={!hasAccess}>
                      <i className="fas fa-download fa-fw"></i>
                    </SmallRoundedIconButton>
                  </MaterialListItem>
                );
              })}
          </MaterialList>
        )}
      </div>
    );
  }
);
